/** @jsxImportSource @emotion/react */
import type { ColDef } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import type { MenuProps } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { nodeActionLabel } from '@sportninja/common/features/competition/operations/utils/nodeActionLabels'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import type { OperationsTeamNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import type { InDivisionRecord } from '@sportninja/common/features/competition/operations/types/standings.types'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import RecordCell from '../components/cells/RecordCell'
import RowActionsCell from '../components/cells/RowActionsCell'
import TeamNameCell from '../components/cells/TeamNameCell'

const translate = t as (key: string, options?: unknown) => string

export type DivisionTeamRow = OperationsTeamNode & {
  pending: boolean
  /**
   * From the standings request, joined on `teamId`. `null` means the team has
   * played nothing; `undefined` means it has no standings entry at all.
   */
  record?: InDivisionRecord | null
  points?: number
}

export type RowAction = 'edit' | 'delete'

interface Options {
  canEdit: boolean
  /**
   * Drops Delete from the menu while registration is open — registered teams
   * belong to the registration flow. Mirrors the tree row's own guard; see
   * `canDeleteNode`.
   */
  isRegistrationOn?: boolean
  onRowAction: (action: RowAction, row: DivisionTeamRow) => void
  /**
   * Drops the standings columns entirely — a division that does not publish
   * standings has nothing to put in them.
   */
  showStandings?: boolean
  /** Busiest team in the division, so every bar shares the chart's scale. */
  maxGamesPlayed?: number
  isStandingsLoading?: boolean
  /**
   * Width of the record column's bar track, measured ONCE by the grid. Passed
   * down so no cell mounts an observer of its own — see `trackWidth` on
   * `RecordCell`.
   */
  recordTrackWidth?: number
  /**
   * Whether the parent holding these teams is a conference rather than a
   * division, which only changes the record column's header. The stat itself
   * (API id 208) is "record within THIS schedule", so "in-division record"
   * misnames it on a conference.
   *
   * Not translated: there is no `common:record` key and adding one to the
   * shared lang files was declined for this feature — the same tradeoff the
   * "Division Performance" tab label already makes.
   */
  isConference?: boolean
}

/**
 * Edit / Delete for a team row. Moving a team is NOT offered here — it lives on
 * the team's own panel header (`MoveTeamSelect`) and as a drag onto another
 * division, so the row menu matches the divisions sheet's.
 */
const buildTeamRowActionItems = (
  isRegistrationOn: boolean
): MenuProps['items'] => [
  {
    key: 'edit',
    icon: <EditOutlined />,
    label: capitalizeWords(nodeActionLabel('edit', 'team', translate)),
  },
  // Omitted, not disabled: legacy hid the button outright rather than showing
  // an unusable one.
  ...(isRegistrationOn
    ? []
    : [
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: capitalizeWords(nodeActionLabel('delete', 'team', translate)),
          danger: true,
        },
      ]),
]

/**
 * Columns for the division's teams sheet. Every value comes from the tree
 * payload already in memory, so the grid renders without another request.
 */
export const useDivisionTeamsColumns = ({
  canEdit,
  isRegistrationOn = false,
  onRowAction,
  showStandings = false,
  maxGamesPlayed = 0,
  isStandingsLoading = false,
  recordTrackWidth = 0,
  isConference = false,
}: Options): ColDef<DivisionTeamRow>[] => {
  'use memo'

  // The same two figures the Division Performance chart shows, so a user does
  // not have to switch tabs to read a team's season at a glance.
  const standingsColumns: ColDef<DivisionTeamRow>[] = showStandings
    ? [
        {
          colId: 'record',
          headerName: capitalizeWords(
            isConference ? 'record' : translate('common:inDivisionRecord')
          ),
          // Sorting is by games played: the record is a five-part string, so
          // AG Grid's own comparator would order it alphabetically.
          valueGetter: ({ data }) => data?.record?.gamesPlayed ?? -1,
          // The bar is the widest thing to draw and the only column that gets
          // better with room, so it absorbs the leftover space. `name` is
          // capped above rather than competing for it, which is what keeps the
          // columns before this one packed together.
          flex: 2,
          minWidth: 360,
          cellRenderer: ({ data }: CustomCellRendererProps<DivisionTeamRow>) =>
            data ? (
              <RecordCell
                teamName={data.name}
                record={data.record}
                maxGamesPlayed={maxGamesPlayed}
                isLoading={isStandingsLoading}
                trackWidth={recordTrackWidth}
              />
            ) : null,
        },
        {
          colId: 'points',
          field: 'points',
          headerName: capitalizeWords(translate('common:pts')),
          // Sized to the HEADER, not the value: the shared DataGrid theme sets
          // `cellHorizontalPadding: 14` (28px a side, header cells included)
          // and `numericColumn` puts the sort arrow inline beside the label, so
          // the 1-3 digits beneath are never what binds. "PTS" at 10px with 1px
          // tracking + arrow + padding needs this much before AG Grid
          // ellipsises the label to "P…". The class trims that padding back
          // so the extra width buys label, not gutter — the record column
          // beside it is the one that gets better with room.
          width: 68,
          headerClass: 'operations-tight-header',
          type: 'numericColumn',
          cellRenderer: ({
            data,
          }: CustomCellRendererProps<DivisionTeamRow>) => {
            if (isStandingsLoading) {
              return <SkeletonText width={24} />
            }
            // No standings entry — blank, not a fabricated 0.
            return data?.points === undefined ? '' : String(data.points)
          },
        },
      ]
    : []

  const actionsColumn: ColDef<DivisionTeamRow>[] = canEdit
    ? [
        {
          colId: 'actions',
          headerName: '',
          // Frozen to the right edge so the menu stays reachable on a wide table.
          pinned: 'right',
          // Just clears the 28px trigger — the column has no header text to
          // fit. The class trims the theme's cell padding, which would
          // otherwise crowd the button at this width.
          width: 48,
          cellClass: 'operations-actions-cell',
          sortable: false,
          resizable: false,
          cellRenderer: ({ data }: CustomCellRendererProps<DivisionTeamRow>) =>
            data ? (
              <RowActionsCell
                items={buildTeamRowActionItems(isRegistrationOn)}
                onSelect={(key) => onRowAction(key as RowAction, data)}
                label={`${data.name} actions`}
              />
            ) : null,
        },
      ]
    : []

  return [
    // Seed rides inside this cell — between the drag grip and the crest —
    // rather than taking a column of its own: it is 1-2 digits, and the record
    // bar uses that width better. It is read-only either way: the API reorders
    // via seed_action 'before'|'after' + destination_team_id (see
    // ScheduleTree/index.jsx:706); there is no absolute-seed write endpoint.
    {
      colId: 'name',
      headerName: capitalizeWords(translate('common:team')),
      // Composite value (name + image + seed), NOT a plain `field: 'name'`.
      //
      // `getRowId` puts AG Grid in immutable-data mode, where a cell is only
      // re-rendered when its column VALUE changes. With the name as that value,
      // editing a team's LOGO changed nothing AG Grid could see, so the stale
      // crest stayed on screen -- while the tree rail, which has no such diff,
      // updated correctly. Everything the cell DRAWS belongs in the value, so
      // the seed is here too: a reorder changes it without touching the name.
      // Separator is NUL, which cannot occur in any part.
      valueGetter: ({ data }: { data?: DivisionTeamRow }) =>
        `${data?.name ?? ''}\u0000${data?.image ?? ''}\u0000${
          data?.seed ?? ''
        }`,
      valueFormatter: ({ data }: { data?: DivisionTeamRow }) =>
        data?.name ?? '',
      // Sort on the real name, not the composite above.
      comparator: (a: string, b: string) =>
        String(a)
          .split('\u0000')[0]
          .localeCompare(String(b).split('\u0000')[0]),
      // Bounded rather than open-ended: unbounded, this and `record` split
      // every spare pixel between them, which on a wide viewport left a gap
      // between the team name and the games count. A team name does not get
      // better past this width; the record bar does, so the slack belongs to
      // it (or, with standings off, to this column as the last data one).
      flex: 1,
      minWidth: 258,
      maxWidth: showStandings ? 420 : undefined,
      // The grip is an AFFORDANCE, not the drag target — `rowDragEntireRow`
      // already makes the whole row draggable, but without a visible grip
      // nothing tells the user that. It leads the cell, ahead of the seed.
      rowDrag: canEdit,
      cellRenderer: ({ data }: CustomCellRendererProps<DivisionTeamRow>) =>
        data ? (
          <TeamNameCell
            name={data.name}
            imageUrl={resolveAssetUrl(data.image)}
            seed={data.seed}
          />
        ) : null,
    },
    {
      colId: 'gamesCount',
      field: 'gamesCount',
      headerName: capitalizeWords(
        translate('common:game_plural', { count: 2 })
      ),
      // Sized to the HEADER, not the value: "GAMES" at 10px with 1px tracking
      // needs more room than the one or two digits beneath it, and AG Grid
      // ellipsises the label rather than wrapping it. The budget is the label
      // plus the shared theme's `cellHorizontalPadding: 14` a side plus the
      // inline sort arrow `numericColumn` adds — 76px left only the "G…". The
      // class trims that padding back so the extra width buys label, not
      // gutter.
      width: 92,
      headerClass: 'operations-tight-header',
      type: 'numericColumn',
    },
    ...standingsColumns,
    ...actionsColumn,
  ]
}
